import type { SyncStatus } from '@wiztime/sync';
import { SyncUiError, SyncUiState } from '../lib/syncState';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { settingsPath, SettingsSection } from '../app/routes';
import { isSyncEnabled } from '../app/sync';
import { useSyncStatus } from '../hooks/useSyncStatus';
import { cx } from '../lib/cx';
import styles from './TopBar.module.css';
import { useSyncStatusText } from './syncText';

const DOT_CLASS: Readonly<Record<SyncStatus['state'], string | undefined>> = {
  [SyncUiState.Disabled]: undefined,
  [SyncUiState.Idle]: styles.syncIdle,
  [SyncUiState.Syncing]: styles.syncSyncing,
  [SyncUiState.Offline]: styles.syncOffline,
  [SyncUiState.Error]: styles.syncError,
  [SyncUiState.SpaceMissing]: styles.syncSpaceMissing,
};

/** The coloured dot itself: synced, syncing, offline, error or cloud data deleted. */
export function SyncDot({ state }: { state: SyncStatus['state'] }) {
  return <span className={cx(styles.syncDot, DOT_CLASS[state])} aria-hidden="true" />;
}

/** The dot as a link to the sync settings, with the status and the last sync time as its label. */
export function SyncStatusDot({ status }: { status: SyncStatus }) {
  const { t } = useTranslation();
  const text = useSyncStatusText(status);
  // Records are held back until the email is verified: say so in words, the dot alone does not explain it.
  const unverified = status.lastError === SyncUiError.EmailUnverified;
  return (
    <Link
      to={settingsPath(SettingsSection.Data)}
      className={cx(styles.syncLink, unverified && styles.syncLinkNote)}
      aria-label={text}
      title={text}
      data-testid="sync-dot"
      data-state={status.state}
    >
      <SyncDot state={status.state} />
      {unverified ? (
        <span className={styles.syncNote} data-testid="sync-unverified-text">
          {t('sync.error.email_unverified')}
        </span>
      ) : null}
    </Link>
  );
}

/** The top bar's sync indicator: nothing while sync is off. */
export function SyncIndicator() {
  const status = useSyncStatus();
  return isSyncEnabled(status) ? <SyncStatusDot status={status} /> : null;
}
